import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/mocks/films";
import { getCategoryName } from "@/mocks/films";

interface HeroCarouselProps {
  films: Film[];
}

export default function HeroCarousel({ films }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % films.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [films.length]);

  const go = (index: number) => {
    setActive((index + films.length) % films.length);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % films.length);
    }, 6000);
  };

  const current = films[active];

  return (
    <section className="relative w-full bg-primary-950">
      <div className="relative w-full h-[560px] md:h-[620px] lg:h-[660px] overflow-hidden">
        {films.map((film, index) => (
          <div
            key={film.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              index === active ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={film.cover}
              alt={`${film.title} 焦点推荐海报`}
              title={`${film.title} · 焦点推荐`}
              className={`w-full h-full object-cover object-top ${
                index === active ? "scale-100" : "scale-105"
              } transition-transform duration-[7000ms]`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/40" />
          </div>
        ))}

        <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
          <div className="w-full max-w-[1280px] mx-auto">
            <div key={current.id} className="max-w-2xl animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/15 border border-accent-500/40 text-[11px] tracking-[0.2em] uppercase text-accent-300">
                <i className="ri-fire-line text-[12px]" />
                本周焦点推荐
              </div>
              <h2 className="mt-5 font-heading text-3xl md:text-5xl font-semibold text-background-50 leading-tight">
                {current.title}
              </h2>
              <p className="mt-2 text-[12px] tracking-[0.22em] uppercase text-background-400">
                {current.titleEn}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-background-300">
                <span className="flex items-center gap-1 text-accent-300 font-semibold">
                  <i className="ri-star-fill text-[11px]" />
                  {current.rating.toFixed(1)}
                </span>
                <span className="text-background-500">|</span>
                <span>{current.year}</span>
                <span className="text-background-500">|</span>
                <span>{current.region}</span>
                <span className="text-background-500">|</span>
                <span>{current.duration} 分钟</span>
                <span className="text-background-500">|</span>
                <span>导演 {current.director}</span>
              </div>
              <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-background-200 max-w-xl">
                {current.summary}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {current.categories.map((slug) => (
                  <Link
                    key={slug}
                    to={`/category/${slug}`}
                    className="px-2.5 py-1 rounded-full bg-background-50/10 border border-background-50/20 text-[12px] text-background-200 hover:bg-background-50/20 transition-colors cursor-pointer"
                  >
                    {getCategoryName(slug)}
                  </Link>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  <i className="ri-play-circle-line text-base" />
                  查看影片详情
                </Link>
                <Link
                  to="/category/drama"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/25 text-background-100 text-sm whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
                >
                  浏览全部分类
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 z-10 px-4 md:px-8">
          <div className="w-full max-w-[1280px] mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {films.map((film, index) => (
                <button
                  key={film.id}
                  type="button"
                  aria-label={`切换到第 ${index + 1} 张焦点图`}
                  onClick={() => go(index)}
                  className={`h-1 rounded-full transition-all cursor-pointer ${
                    index === active ? "w-10 bg-accent-400" : "w-5 bg-background-50/30 hover:bg-background-50/60"
                  }`}
                />
              ))}
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => go(active - 1)}
                className="w-9 h-9 rounded-full border border-background-50/25 text-background-200 flex items-center justify-center hover:bg-background-50/10 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <span className="text-[12px] font-mono text-background-400 w-12 text-center">
                {String(active + 1).padStart(2, "0")} / {String(films.length).padStart(2, "0")}
              </span>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => go(active + 1)}
                className="w-9 h-9 rounded-full border border-background-50/25 text-background-200 flex items-center justify-center hover:bg-background-50/10 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}